import { useEffect } from "react";

/* Per-route metadata for a client-rendered app.

   The static tags in public/index.html describe the index page and are what
   non-JavaScript crawlers (most AI crawlers) read. This keeps the document
   head correct for crawlers that *do* run JavaScript - Googlebot above all -
   as the router moves between routes.

   Anything written here is marked data-seo so it can be cleared on the way
   out, instead of accumulating stale tags across navigations. */

export const SITE = "https://www.devtimmy.xyz";

const upsert = (selector, create, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    el.setAttribute("data-seo", "");
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};

const meta = (name, content) =>
  content &&
  upsert(`meta[name="${name}"]`, () => document.createElement("meta"), {
    name,
    content,
  });

const property = (prop, content) =>
  content &&
  upsert(
    `meta[property="${prop}"]`,
    () => document.createElement("meta"),
    { property: prop, content }
  );

const Seo = ({
  title,
  description,
  path = "/",
  image = `${SITE}/og-image.jpg`,
  type = "website",
  schema,
}) => {
  useEffect(() => {
    const url = `${SITE}${path === "/" ? "/" : path}`;

    if (title) document.title = title;

    meta("description", description);
    meta("robots", "index, follow, max-snippet:-1, max-image-preview:large");

    upsert('link[rel="canonical"]', () => document.createElement("link"), {
      rel: "canonical",
      href: url,
    });

    property("og:type", type);
    property("og:url", url);
    property("og:title", title);
    property("og:description", description);
    property("og:image", image);

    meta("twitter:card", "summary_large_image");
    meta("twitter:title", title);
    meta("twitter:description", description);
    meta("twitter:image", image);

    // Route-scoped structured data, replaced on every route change.
    let node = null;
    if (schema) {
      node = document.createElement("script");
      node.type = "application/ld+json";
      node.setAttribute("data-seo-schema", "");
      node.textContent = JSON.stringify(schema);
      document.head.appendChild(node);
    }

    return () => {
      if (node && node.parentNode) node.parentNode.removeChild(node);
    };
  }, [title, description, path, image, type, schema]);

  return null;
};

export default Seo;
